/**
 * `wpp::notifications`
 *
 * The queue behind `<x-wpp::notifications/>`. One of these sits in a layout and
 * every component under it dispatches to the same one — see
 * `Bernskiold\WppUiBlade\Notification`.
 *
 * ## Five seconds, and no hover-pause
 *
 * ODS says a toast disappears after five seconds and does not describe a
 * pause-on-hover. That is a real decision rather than an omission: the
 * specification also says whatever a toast offers must still be reachable in the
 * interface after it has gone, so nothing is lost by it leaving on time. Holding
 * it under the pointer would make the one thing on screen that takes itself away
 * behave differently depending on where the mouse happened to be resting.
 *
 * ## New ones go underneath
 *
 * Appended rather than prepended, because the specification puts a new toast
 * below the ones already there. The container is a plain column, so append is
 * all it takes.
 *
 * ## Identity is a counter, not the text
 *
 * Two identical messages are two events and both should show. Keying by content
 * would silently swallow the second, which on a repeated action reads as the
 * action not having worked.
 */
/* `maxToastsToDisplay`, whose ODS default is four. */
const LIMIT = 4;

/*
 * `staggerInterval`. ODS holds each toast back so that a burst arrives one by
 * one on a continuous timeline rather than as a batched "wave" — which is what
 * makes a loop that raises several read as a sequence of things happening rather
 * than as one thing going wrong four times.
 */
const STAGGER = 200;

export default () => ({
    notifications: [],
    nextId: 1,
    lastShownAt: 0,

    init() {
        window.addEventListener('wpp-notification', (event) => {
            this.push(event.detail?.[0] ?? event.detail ?? {});
        });
    },

    push(notification) {
        if (!notification.title) {
            return;
        }

        /*
         * Held back to the stagger interval since the last one appeared. `now`
         * for the first of a burst, so a single toast is never delayed.
         */
        const now = Date.now();
        const wait = Math.max(0, this.lastShownAt + STAGGER - now);

        this.lastShownAt = now + wait;

        setTimeout(() => this.show(notification), wait);
    },

    show({ title, body = null, variant = 'success', duration = 5000, action = null }) {
        const id = this.nextId++;

        this.notifications.push({ id, title, body, variant, duration, action, timer: null });

        /* Oldest out first — a loop raising one per iteration should not fill the screen. */
        while (this.notifications.length > LIMIT) {
            this.dismiss(this.notifications[0].id);
        }

        this.start(id);
    },

    start(id) {
        const notification = this.find(id);

        /* A duration of nothing is a toast that waits to be dismissed. */
        if (!notification || notification.duration <= 0) {
            return;
        }

        notification.timer = setTimeout(() => this.dismiss(id), notification.duration);
    },

    dismiss(id) {
        const notification = this.find(id);

        if (notification?.timer) {
            clearTimeout(notification.timer);
        }

        this.notifications = this.notifications.filter((notification) => notification.id !== id);
    },

    find(id) {
        return this.notifications.find((notification) => notification.id === id);
    },
});
